import React, { memo, ReactNode, useMemo } from 'react';
import Button from '../../../design-system/ui/button';
import { accessibleOnClick } from '../../../utils';

export interface DialerControlsHeaderProps {
  /** Avatar content - string (e.g. initials) or ReactNode */
  avatarContent: ReactNode;
  /** Prospect/contact name */
  name: string;
  /** Phone number or secondary text */
  phone: string;
  /** Dynamic icon - pass with desired width/height, e.g. <DialerEndCall width={24} height={24} /> */
  icon: ReactNode;
  /** Callback when icon is clicked */
  onIconClick: () => void;
  /** Optional class name for the icon container */
  iconContainerClassName?: string;
  /** When > 0, shows "+X more" link beside phone. Click triggers onMoreClick */
  extraPhoneCount?: number;
  /** Callback when "+X more" is clicked */
  onMoreClick?: () => void;
  disabled?: boolean;
}

const DialerControlsHeader: React.FC<DialerControlsHeaderProps> = ({
  avatarContent,
  name,
  phone,
  icon,
  onIconClick,
  iconContainerClassName = 'dialer-end-call-icon-container',
  extraPhoneCount = 0,
  onMoreClick,
  disabled = false,
}) => {
  const isNameSameAsPhone = useMemo(() => name === phone, [name, phone]);

  return (
    <div className="dialer-ringing-controls-header">
      <div className="dialer-ringing-avatar">{avatarContent}</div>
      <div className="prospect-details-wrapper">
        <p className="prospect-name">{name}</p>
        <div className="prospect-phone-row">
          {isNameSameAsPhone ? null : <p className="prospect-phone">{phone}</p>}
          {extraPhoneCount > 0 && onMoreClick && (
            <button
              type="button"
              className="prospect-phone-more"
              onClick={(e) => {
                e.stopPropagation();
                onMoreClick();
              }}
            >
              +{extraPhoneCount} more
            </button>
          )}
        </div>
      </div>
      <Button
        type="button"
        {...accessibleOnClick(onIconClick)}
        className={iconContainerClassName}
        disabled={disabled}
      >
        {icon}
      </Button>
    </div>
  );
};
export default memo(DialerControlsHeader);
